import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Calendar, ExternalLink } from 'lucide-react';
import { SUMMIT_CONFIG } from '../config/summitConfig';

interface AboutSectionProps {
  reducedMotion: boolean;
}

export const AboutSection: React.FC<AboutSectionProps> = ({ reducedMotion }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="about" 
      ref={sectionRef}
      className="relative py-28 sm:py-36 bg-gradient-to-b from-deepblack via-[#141414] to-deepblack overflow-hidden perspective-stage border-t border-white/5"
    >
      {/* Decorative Classical Background Column Silhouettes */}
      <div className="absolute inset-0 pointer-events-none opacity-20 flex justify-between px-8 sm:px-24">
        {/* Left Colonnade silhouette */}
        <div 
          className="w-16 h-full border-x border-beige/20 fluted-column hidden md:block transition-transform duration-1000"
          style={{
            transform: reducedMotion ? 'none' : isVisible ? 'translateY(0)' : 'translateY(60px)'
          }}
        />
        {/* Center subtle arch outline */}
        <div className="w-[500px] h-full border-t border-x border-beige/10 rounded-t-full hidden lg:block opacity-30" />
        {/* Right Colonnade silhouette */}
        <div 
          className="w-16 h-full border-x border-beige/20 fluted-column hidden md:block transition-transform duration-1000"
          style={{
            transform: reducedMotion ? 'none' : isVisible ? 'translateY(0)' : 'translateY(-60px)'
          }}
        />
      </div>

      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-32 relative z-10">
        
        {/* Conference Venue & Schedule Banner */}
        <div id="venue" className="mb-14 sm:mb-16 scroll-mt-24">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-[1px] bg-bronze" />
            <span className="text-xs uppercase tracking-architectural text-bronze font-semibold">
              WHEN & WHERE
            </span>
            <div className="w-12 h-[1px] bg-bronze/40" />
          </div>
          
          <h2 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-ivory mb-6">
            EVENT DETAILS
          </h2>

          <div className="p-6 sm:p-8 bg-charcoal-subtle/70 border border-bronze/30 rounded-sm grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 items-center shadow-lg divide-y md:divide-y-0 md:divide-x divide-white/10">
            <a
              href={SUMMIT_CONFIG.venueMapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group/venue transition-colors pb-4 md:pb-0"
              data-cursor-interactive
              data-cursor-text="MAP"
            >
              <div className="p-3.5 rounded-full border border-bronze/40 text-beige bg-deepblack shrink-0 group-hover/venue:border-beige group-hover/venue:text-deepblack group-hover/venue:bg-beige transition-all shadow-md">
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] text-ivory/50 uppercase tracking-widest font-bold flex items-center gap-1.5">
                  CONFERENCE VENUE
                  <ExternalLink className="w-3.5 h-3.5 text-bronze group-hover/venue:text-beige transition-colors" />
                </span>
                <span className="text-xs sm:text-sm uppercase tracking-wider text-beige font-mono font-bold group-hover/venue:text-white transition-colors block mt-0.5">
                  {SUMMIT_CONFIG.venue}
                </span>
                <span className="text-[10px] text-ivory/40 block mt-1 group-hover/venue:text-beige/80 transition-colors">
                  Open in Google Maps ↗
                </span>
              </div>
            </a>

            <div className="flex items-center gap-4 pt-4 md:pt-0 md:pl-10">
              <div className="p-3.5 rounded-full border border-bronze/40 text-beige bg-deepblack shrink-0 shadow-md">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] text-ivory/50 uppercase tracking-widest font-bold block">
                  CONFERENCE DATES
                </span>
                <span className="text-xs sm:text-sm uppercase tracking-widest text-beige font-mono font-bold block mt-0.5">
                  {SUMMIT_CONFIG.datesFull}
                </span>
                <span className="text-[10px] text-ivory/40 block mt-1">
                  Model United Nations Simulation
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Section Pre-title */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-[1px] bg-bronze" />
          <span className="text-xs uppercase tracking-architectural text-bronze font-semibold">
            THE SUMMIT
          </span>
          <div className="w-12 h-[1px] bg-bronze" />
        </div>

        {/* Main Statement */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <h2 
              className={`font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ivory leading-[1.05] transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              THE NEXT GENERATION <br />
              <span className="text-beige font-light italic">OF LEADERS.</span>
            </h2>

            {/* Classical Horizontal Divider with Meander */}
            <div className="w-full h-2 greek-key-border-subtle my-8 opacity-60" />

            <div className="space-y-6 text-ivory/80 text-base sm:text-lg font-light leading-relaxed">
              <p 
                className={`transition-all duration-700 delay-150 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                {SUMMIT_CONFIG.aboutParagraphOne}
              </p>
              <p 
                className={`transition-all duration-700 delay-300 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                {SUMMIT_CONFIG.aboutParagraphTwo}
              </p>
            </div>
          </div>

          {/* Animated Statistics Cards */}
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4 lg:pt-8">
            {SUMMIT_CONFIG.statistics.map((stat, idx) => (
              <div
                key={stat.label}
                className={`marble-card p-6 rounded-sm flex flex-col justify-between relative overflow-hidden transition-all duration-700 transform hover:-translate-y-2`}
                style={{
                  transitionDelay: `${idx * 200 + 400}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible 
                    ? 'translateY(0) scale(1)' 
                    : 'translateY(30px) scale(0.95)',
                }}
                data-cursor-interactive
              >
                {/* Subtle Top Accent */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-bronze to-transparent" />
                
                <div className="mb-4">
                  <span className="font-serif text-5xl sm:text-6xl font-light text-beige tracking-tight block">
                    {stat.value}
                  </span>
                </div>

                <div>
                  <h3 className="text-xs uppercase tracking-architectural text-ivory font-semibold mb-1">
                    {stat.label}
                  </h3>
                  <p className="text-[11px] text-ivory/50 leading-snug">
                    {stat.description}
                  </p>
                </div>

                {/* Classical Pedestal Footing */}
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[9px] font-mono text-bronze uppercase">
                  <span>FLS • 2026</span>
                  <span>FLSxMUN 2.0</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};
